import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, ChevronRight, ChevronDown, Calendar, Clock, AlertTriangle, PhoneCall, Pencil, Plus, ArrowLeft, User } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';
import { ConfirmDialog, type ConfirmTone } from '@/components/ConfirmDialog';
import { BTN_GHO, BTN_PRI, BTN_SEC, BTN_SM, FIELD_LABEL, INP, Kicker } from '@/components/brand/brand';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { useBookings } from '@/features/bookings/useBookings';
import { bookingsApi, type BookingRow, type PatientSearchResult } from '@/features/bookings/bookings.api';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';
import { ApiError, apiCall } from '@/lib/api-client';
import { QUEUE_KEY } from '@/features/queue/useQueue';

interface BookingsListModalProps {
  open: boolean;
  onClose: () => void;
}

type Filter = 'today' | 'week' | 'upcoming' | 'past' | 'all';

const TABS: Array<{ key: Filter; label: string; tone: string }> = [
  { key: 'today', label: 'Today', tone: 'lavender' },
  { key: 'week', label: 'This week', tone: 'sky' },
  { key: 'upcoming', label: 'Upcoming', tone: 'mint' },
  { key: 'past', label: 'Past', tone: 'ink' },
  { key: 'all', label: 'All', tone: 'peach' },
];

// F5-R1b — mismas claves de estado, valores al brand. F5-R9g: la forma pasa
// al chip redondo del navigator (TypeChip) y completada se une al teal del
// catálogo (encargo verbatim); perdida = red y cancelada = neutro conservan
// su semántica de tokens nav-*.
const STATUS_PILL: Record<string, string> = {
  scheduled: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  completed: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  missed: 'bg-nav-redTint text-nav-red border-nav-red/40',
  rescheduled: 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
  cancelled: 'bg-nav-hair text-nav-slate border-nav-card',
};

/** F5-R9g — chip de estado redondo del navigator (forma de TypeChip). */
const STATUS_CHIP = 'font-head font-bold text-[11px] px-[11px] py-1 rounded-full border inline-block flex-none';

export function BookingsListModal({ open, onClose }: BookingsListModalProps): JSX.Element | null {
  const t = useT();

  if (!open) return null;

  return (
    <ModalShell open={open} onClose={onClose} ariaLabelledBy="bookings-modal-title">
      <header className="sticky top-0 bg-white border-b border-nav-card px-6 py-5 flex items-center justify-between rounded-t-lg z-10">
        <div>
          <h2 id="bookings-modal-title" className="font-head text-xl font-bold text-nav-navy tracking-tight">
            {t('bookings.modal.title')}
          </h2>
          <p className="text-xs text-nav-slate mt-0.5">{t('bookings.modal.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" className={BTN_GHO} onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto">
        <div className="px-6 py-4">
          <BookingsPanel autoFocus />
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * F5-R8a — el CUERPO vivo de «Mis citas», extraído para que la pestaña
 * «Mi agenda» lo monte como página y el modal siga funcionando idéntico:
 * tabs de rango, búsqueda por apellido/MRN, toggle de historial (A-14),
 * detalle expandible con start/reschedule/notas/estados y el creador inline.
 * Cero lógica nueva — solo cambió la cáscara. El botón «+ Nueva cita»
 * (data-testid intacto) vive ahora en la barra de herramientas del panel.
 */
export function BookingsPanel({ autoFocus = false }: { autoFocus?: boolean }): JSX.Element {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // 2026-05-09: inline "+ New booking" creator. When true, the modal body
  // shows the create form INSTEAD of the list (back button returns to list).
  const [creatorOpen, setCreatorOpen] = useState<boolean>(false);
  // A-14 (2026-05-14): hide completed/cancelled bookings by default so the
  // active list only shows actionable rows. Toggle exposes the full history
  // (useful for admin/supervisor review and "did I screen this patient?").
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const bookingsQ = useBookings(filter);

  // Filter bookings client-side by:
  //   - history toggle (default: hide completed + cancelled)
  //   - patient last name OR MRN substring (case-insensitive)
  const filteredBookings = useMemo(() => {
    const rows = bookingsQ.data ?? [];
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((b) => {
      // A-14: hide terminal statuses unless showHistory is on
      if (!showHistory && (b.status === 'completed' || b.status === 'cancelled')) {
        return false;
      }
      if (!q) return true;
      const lastName = (b.patient?.lastNameDecrypted ?? '').toLowerCase();
      const mrn = (b.patient?.mrnLast6 ?? '').toLowerCase();
      return lastName.includes(q) || mrn.includes(q);
    });
  }, [bookingsQ.data, searchQuery, showHistory]);

  // Hidden count (for the toggle label) — how many rows would re-appear if
  // history is enabled? Helps the user notice that a completed booking exists.
  const hiddenHistoryCount = useMemo(() => {
    const rows = bookingsQ.data ?? [];
    return rows.filter((b) => b.status === 'completed' || b.status === 'cancelled').length;
  }, [bookingsQ.data]);

  useEffect(() => {
    if (!autoFocus) return;
    dialogRef.current?.focus();
  }, [autoFocus]);

  // Reset expansion when filter or search changes
  useEffect(() => {
    setExpandedId(null);
  }, [filter, searchQuery]);

  return (
        <div ref={dialogRef} tabIndex={-1}>
          {creatorOpen && (
            <CreateBookingForm
              onCancel={() => setCreatorOpen(false)}
              onSuccess={() => setCreatorOpen(false)}
            />
          )}
          {!creatorOpen && (
            <>
          <div className="border-b border-nav-hair pb-4 mb-4 overflow-x-auto flex items-center justify-between gap-3 flex-wrap">
            {/* F5-R9g — tabs de rango como cápsulas del chasis (patrón Pager,
                44px táctil): activa navy sólido, resto blancas con borde. */}
            <div role="tablist" className="flex flex-wrap items-center gap-1.5">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={filter === tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={cn(
                    'min-h-[44px] px-4 rounded-lg border font-head text-[13px] font-semibold transition-colors whitespace-nowrap',
                    filter === tab.key
                      ? 'bg-nav-navy text-white border-nav-navy'
                      : 'bg-white text-nav-slate border-nav-line hover:bg-nav-softer hover:text-nav-navy',
                  )}
                >
                  {t(`bookings.tab.${tab.key}`)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {/* A-14 hotfix (Bug A): only render the history toggle when there is
                  actually something to toggle. In tabs like "Próximas" / "Hoy" /
                  "Esta semana" the backend filter never returns completed/cancelled
                  rows, so the button was a no-op that confused users. We also
                  keep it visible while showHistory is on (so the user can hide
                  back to default), otherwise hide it. */}
              {(hiddenHistoryCount > 0 || showHistory) && (
                <button
                  type="button"
                  onClick={() => setShowHistory((v) => !v)}
                  className={cn(BTN_SEC, BTN_SM)}
                  data-testid="bookings-history-toggle"
                >
                  {showHistory
                    ? t('bookings.history.hide')
                    : `${t('bookings.history.show')} (${hiddenHistoryCount})`}
                </button>
              )}
              <button
                type="button"
                onClick={() => setCreatorOpen(true)}
                className={BTN_GHO}
                aria-label={t('bookings.modal.create.button')}
                data-testid="bookings-new-btn"
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('bookings.modal.create.button')}
              </button>
            </div>
          </div>

          {/* Search input — filters by patient last name OR MRN. */}
          {bookingsQ.data && bookingsQ.data.length > 0 && (
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-nav-faint pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setSearchQuery('');
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  placeholder={t('bookings.search.placeholder')}
                  aria-label={t('bookings.search.placeholder')}
                  data-testid="bookings-search-input"
                  className={cn(INP, 'pl-8 pr-9')}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('bookings.search.clear')}
                    data-testid="bookings-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-nav-faint hover:text-nav-navy hover:bg-nav-hair"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* F5-R9g — cargando: esqueletos del tamaño real de fila (76px);
              el spinner centrado murió con el contrato del chasis. */}
          {bookingsQ.isLoading && (
            <div className="flex flex-col gap-2.5 py-2" aria-hidden>
              <Skeleton className="h-[76px] w-full rounded-[10px]" />
              <Skeleton className="h-[76px] w-full rounded-[10px]" />
              <Skeleton className="h-[76px] w-full rounded-[10px]" />
            </div>
          )}

          {bookingsQ.error && (
            <ErrorState
              icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
              title={t('agentF5.r9g.errorTitle')}
              body={t('agentF5.r9g.errorBody')}
              action={{ label: t('agentF5.r9g.retry'), onClick: () => { void bookingsQ.refetch(); } }}
            />
          )}

          {/* Estado vacío en salvia, sin botón — muere el borde dashed. */}
          {bookingsQ.data && bookingsQ.data.length === 0 && (
            <EmptyState
              icon={<Calendar className="h-5 w-5" aria-hidden />}
              title={t('bookings.empty')}
              body={t('agentF5.r9g.emptyBody')}
            />
          )}

          {bookingsQ.data && bookingsQ.data.length > 0 && filteredBookings.length === 0 && (
            <div data-testid="bookings-search-empty">
              <EmptyState
                icon={<Search className="h-5 w-5" aria-hidden />}
                title={t('agentF5.r9g.searchEmptyTitle')}
                body={t('bookings.search.empty', { query: searchQuery })}
              />
            </div>
          )}

          {filteredBookings.length > 0 && (
            <>
              {/* F5-R9g — kicker de sección con el conteo real de la vista. */}
              <Kicker>{t(`bookings.tab.${filter}`)} · {filteredBookings.length}</Kicker>
              {/* Filas-tarjeta del navigator: 76px, borde nav-card, radio 10,
                  stagger fadeUp 60ms por fila (motion-reduce lo apaga). El
                  detalle expandible vive DENTRO de la misma tarjeta. */}
              <div className="flex flex-col gap-2.5">
                {filteredBookings.map((b, ri) => (
                  <div
                    key={b.id}
                    className="bg-white border-[1.5px] border-nav-card rounded-[10px] overflow-hidden animate-fade-up-slow motion-reduce:animate-none"
                    style={{ animationDelay: `${ri * 0.06}s` }}
                  >
                    <BookingRow
                      booking={b}
                      expanded={expandedId === b.id}
                      onToggle={() =>
                        setExpandedId((prev) => (prev === b.id ? null : b.id))
                      }
                    />
                    {expandedId === b.id && (
                      <BookingDetailPanel
                        booking={b}
                        onClose={() => setExpandedId(null)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
            </>
          )}
        </div>
  );
}

function BookingRow({
  booking,
  expanded,
  onToggle,
}: {
  booking: BookingRow;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const t = useT();
  const scheduledDate = new Date(booking.scheduledAt);
  const timeStr = scheduledDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
  const dateStr = scheduledDate.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });

  // F5-R9g — cápsulas de día/hora centradas con ancho fijo y tabular-nums,
  // el patrón de FilaAgenda del navigator.
  const capsula =
    'flex items-center justify-center h-[44px] px-2.5 rounded-lg bg-nav-hair '
    + 'font-head text-[12.5px] font-bold text-nav-navy tabular-nums flex-none';

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className={cn(
        'w-full text-left flex flex-wrap items-center gap-3 min-h-[76px] px-[18px] py-3.5 bg-white transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal/40',
        expanded ? 'bg-nav-tealTint/60' : 'hover:bg-nav-tealTint/30',
      )}
    >
      <span className={cn(capsula, 'min-w-[56px]')}>{dateStr}</span>
      <span className={cn(capsula, 'min-w-[64px]')}>{timeStr}</span>
      <span className="min-w-[160px] flex-1">
        <span className="block font-head text-[14px] font-bold text-nav-navy">
          {booking.patient?.lastNameDecrypted ?? '?'}
        </span>
        <span className="block text-[13px] text-nav-slate font-mono">
          {booking.patient?.mrnLast6 ? '…' + booking.patient.mrnLast6 : ''}
        </span>
      </span>
      <span
        className={cn(
          STATUS_CHIP,
          STATUS_PILL[booking.status] || 'bg-nav-hair text-nav-slate border-nav-card',
        )}
      >
        {t(`bookings.status.${booking.status}`)}
      </span>
      {expanded ? (
        <ChevronDown className="h-4 w-4 text-nav-faint shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 text-nav-faint shrink-0" />
      )}
    </button>
  );
}

/* ----------------------------- Detail panel ------------------------------ */

function relativeFromNow(
  iso: string,
  t: ReturnType<typeof useT>,
): string {
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const absMin = Math.round(Math.abs(diffMs) / 60000);

  if (absMin < 1) return t('bookings.detail.relative.now');
  if (absMin < 60) {
    return diffMs >= 0
      ? t('bookings.detail.relative.minIn', { n: absMin })
      : t('bookings.detail.relative.minAgo', { n: absMin });
  }
  const absHour = Math.round(absMin / 60);
  if (absHour < 24) {
    return diffMs >= 0
      ? t('bookings.detail.relative.hourIn', { n: absHour })
      : t('bookings.detail.relative.hourAgo', { n: absHour });
  }
  const absDay = Math.round(absHour / 24);
  return diffMs >= 0
    ? t('bookings.detail.relative.dayIn', { n: absDay })
    : t('bookings.detail.relative.dayAgo', { n: absDay });
}

function BookingDetailPanel({
  booking,
  onClose,
}: {
  booking: BookingRow;
  onClose: () => void;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newNotes, setNewNotes] = useState(booking.notes ?? '');
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState<null | 'completed' | 'missed' | 'cancelled'>(null);
  // Inline notes edit (in-place, separate from reschedule)
  const [notesEditing, setNotesEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(booking.notes ?? '');
  const [notesError, setNotesError] = useState('');

  const updateMut = useMutation({
    mutationFn: async (data: { status?: string; scheduledAt?: string; notes?: string }) => {
      await bookingsApi.update(booking.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings.me'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['bookings.due-soon'] });
      setRescheduleOpen(false);
      setNewDate('');
      setNewTime('');
      setError('');
    },
    onError: (err) => {
      toast.error('Error', (err as Error).message);
    },
  });

  const notesMut = useMutation({
    mutationFn: async (notesValue: string) => {
      await bookingsApi.update(booking.id, { notes: notesValue });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings.me'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['bookings.due-soon'] });
      setNotesEditing(false);
      setNotesError('');
      toast.success(t('bookings.detail.notes.savedToast'));
    },
    onError: (err) => {
      const msg = (err as Error).message;
      setNotesError(msg);
      toast.error('Error', msg);
    },
  });

    const navigate = useNavigate();
  // Conflict state for AGENT_HAS_ACTIVE_CALL (Cluster A-followup-v2 — A-11).
  // When set, render an inline 3-action alert instead of just a toast.
  const [conflict, setConflict] = useState<{
    conflictingAssignmentId: string;
    conflictingClientId: string;
  } | null>(null);

  const releaseAndRetryMut = useMutation({
    mutationFn: async (conflictingAssignmentId: string) => {
      await apiCall<{ ok: boolean }>(
        `/agents/me/queue/${encodeURIComponent(conflictingAssignmentId)}/release`,
        { method: 'POST' },
      );
    },
    // Cluster A-followup-v2-fixes — A-11: invalidate the queue cache before
    // retrying. Backend release + retry is race-free at the SQL level (verified
    // runtime: serial curl release→start gap = ~27ms, both 200/201), but the
    // queue cache shows the released assignment back as pending only after an
    // explicit invalidate. Defensive UX correctness: prevents the queue list
    // tile from displaying stale in_progress when the modal navigates away.
    onSuccess: async () => {
      // v0.6.1 Fase 10 — fixed phantom key (was ['agents.me.queue'];
      // canonical is QUEUE_KEY=['queue','me']).
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      setConflict(null);
      startMut.mutate();
    },
    onError: (err) => {
      toast.error('Error', (err as Error).message);
    },
  });

  const startMut = useMutation({
    mutationFn: () => bookingsApi.startScreening(booking.id),
    onSuccess: ({ assignmentId }) => {
      setConflict(null);
      // Close the modal, then navigate to pre-call (mirrors queue claim flow).
      onClose();
      navigate(`/pre-call/${assignmentId}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const payload = err.payload as Record<string, any> | null;
        // Nest's ConflictException wraps our object inside payload.message, so the
        // shape is: { statusCode: 409, message: { code, message, ... }, error: 'Conflict' }
        // We accept either the nested shape (Nest default) or a flat shape, just in case.
        const inner = payload?.message;
        const code =
          (typeof inner === 'object' && inner?.code) ||
          payload?.code ||
          null;
        const conflictPayload = (typeof inner === 'object' ? inner : payload) ?? {};
        if (code === 'BOOKING_ALREADY_FINALIZED') {
          toast.error('Error', t('bookings.detail.action.startToast.error.finalized'));
          return;
        }
        if (code === 'PATIENT_ASSIGNED_TO_OTHER_AGENT') {
          toast.error('Error', t('bookings.detail.action.startToast.error.assignedOther'));
          return;
        }
        if (code === 'AGENT_HAS_ACTIVE_CALL') {
          // Cluster A-followup-v2 — A-11: open the inline conflict alert so
          // the agent can either go back to their current call, release it
          // and open this one, or dismiss.
          setConflict({
            conflictingAssignmentId: String(conflictPayload.conflictingAssignmentId ?? ''),
            conflictingClientId: String(conflictPayload.conflictingClientId ?? ''),
          });
          return;
        }
      }
      toast.error('Error', t('bookings.detail.action.startToast.error.generic'));
    },
  });

  const canStart = booking.status === 'scheduled' || booking.status === 'rescheduled';

  const scheduledDate = useMemo(() => new Date(booking.scheduledAt), [booking.scheduledAt]);
  const longDate = scheduledDate.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
  const longTime = scheduledDate.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
  const relSchedule = relativeFromNow(booking.scheduledAt, t);

  const createdAbs = new Date(booking.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
  const createdRel = relativeFromNow(booking.createdAt, t);
  const updatedRel = relativeFromNow(booking.updatedAt, t);

  const agentLabel = t('bookings.detail.agentYou');

  const handleReschedule = (): void => {
    setError('');
    if (!newDate || !newTime) {
      setError(t('bookings.detail.reschedule.errorRequired'));
      return;
    }
    const dt = new Date(newDate + 'T' + newTime);
    if (dt <= new Date()) {
      setError(t('bookings.detail.reschedule.errorPast'));
      return;
    }
    updateMut.mutate({
      scheduledAt: dt.toISOString(),
      notes: newNotes || undefined,
    });
  };

  return (
    <div
      className={cn(
        'border-t border-[#c9e2e2] bg-nav-tealTint/40 px-4 py-4',
        'shadow-inner',
      )}
    >
      <div className="space-y-4">
        {/* Section 1 — Patient header */}
        <div className="flex items-center flex-wrap gap-2">
          <span className={cn(FIELD_LABEL, 'mb-0')}>
            {t('bookings.detail.patientLabel')}
          </span>
          <span className="font-head text-[15px] font-bold text-nav-navy">
            {booking.patient?.lastNameDecrypted ?? '?'}
          </span>
          {booking.patient?.mrnLast6 && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2]">
              MRN: {booking.patient.mrnLast6}
            </span>
          )}
          <Link
            to={`/clients/${booking.patientId}`}
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-nav-tealDark hover:text-nav-navy hover:underline underline-offset-2"
            title={t('bookings.detail.viewProfile')}
          >
            <User className="h-3 w-3" aria-hidden />
            {t('bookings.detail.viewProfile')}
          </Link>
        </div>

        {/* Section 2 — Schedule */}
        <div className="rounded-[9px] border border-[#e6cf9a] bg-nav-amberTint/70 px-4 py-3">
          <div className={cn(FIELD_LABEL, 'mb-1 text-nav-amberText')}>
            {t('bookings.detail.scheduledLabel')}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4">
            <div className="flex items-center gap-2 font-head text-[15px] font-bold text-nav-navy">
              <Calendar className="h-4 w-4 text-nav-amberText" />
              {longDate}
            </div>
            <div className="flex items-center gap-2 font-head text-[15px] font-bold text-nav-navy tabular-nums">
              <Clock className="h-4 w-4 text-nav-amberText" />
              {longTime}
            </div>
          </div>
          <p className="text-xs text-nav-slate mt-1">{relSchedule}</p>
        </div>

        {/* Section 3 — Status */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn(FIELD_LABEL, 'mb-0')}>
            {t('bookings.detail.statusLabel')}
          </span>
          <span
            className={cn(
              STATUS_CHIP,
              STATUS_PILL[booking.status] || 'bg-nav-hair text-nav-slate border-nav-card',
            )}
          >
            {t(`bookings.status.${booking.status}`)}
          </span>
          {booking.status === 'missed' && (
            <span className="inline-flex items-center gap-1 font-head font-bold text-[11px] text-nav-red bg-nav-redTint border border-nav-red/40 px-[11px] py-1 rounded-full">
              <AlertTriangle className="h-3 w-3" />
              {t('bookings.detail.missedAuto')}
            </span>
          )}
        </div>

        {/* Section 4 — Notes (with inline edit) */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className={cn(FIELD_LABEL, 'mb-0')}>
              {t('bookings.detail.notesLabel')}
            </div>
            {!notesEditing && (
              <button
                type="button"
                onClick={() => {
                  setEditedNotes(booking.notes ?? '');
                  setNotesError('');
                  setNotesEditing(true);
                }}
                disabled={updateMut.isPending || notesMut.isPending}
                data-testid="bookings-notes-edit-btn"
                className="inline-flex items-center gap-1 text-[11px] text-nav-tealDark hover:text-nav-navy hover:underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Pencil className="h-3 w-3" />
                {t('bookings.detail.notes.edit')}
              </button>
            )}
          </div>

          {!notesEditing && (
            booking.notes && booking.notes.trim().length > 0 ? (
              <div className="rounded-md border border-nav-card bg-white px-3 py-2 text-sm text-nav-navy whitespace-pre-wrap break-words">
                {booking.notes}
              </div>
            ) : (
              <p className="text-sm text-nav-faint italic">
                {t('bookings.detail.notesEmpty')}
              </p>
            )
          )}

          {notesEditing && (
            <div className="space-y-2">
              {notesError && (
                <Alert variant="error">
                  <AlertDescription>{notesError}</AlertDescription>
                </Alert>
              )}
              <textarea
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value.slice(0, 500))}
                maxLength={500}
                rows={3}
                disabled={notesMut.isPending}
                data-testid="bookings-notes-edit-textarea"
                className={cn(INP, 'resize-none')}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className={cn(BTN_PRI, BTN_SM)}
                  onClick={() => notesMut.mutate(editedNotes)}
                  disabled={notesMut.isPending}
                  data-testid="bookings-notes-save-btn"
                >
                  {t('bookings.detail.notes.save')}
                </button>
                <button
                  type="button"
                  className={cn(BTN_SEC, BTN_SM)}
                  onClick={() => {
                    setNotesEditing(false);
                    setEditedNotes(booking.notes ?? '');
                    setNotesError('');
                  }}
                  disabled={notesMut.isPending}
                  data-testid="bookings-notes-cancel-btn"
                >
                  {t('bookings.detail.notes.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section 5 — Audit footer */}
        <div className="text-[11px] text-nav-slate leading-relaxed border-t border-nav-hair pt-3 space-y-0.5">
          <div>
            {t('bookings.detail.created')} {agentLabel} · {createdAbs} ({createdRel})
          </div>
          <div>
            {t('bookings.detail.lastUpdated')}: {updatedRel}
          </div>
          <div>
            {t('bookings.detail.bookingId')}: #{booking.id}
          </div>
        </div>

        {/* AGENT_HAS_ACTIVE_CALL conflict alert (Cluster A-followup-v2 — A-11) */}
        {conflict && (
          <Alert variant="warning" role="alert">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <AlertTitle>{t('bookings.detail.conflict.title')}</AlertTitle>
              <AlertDescription>{t('bookings.detail.conflict.body')}</AlertDescription>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  className={cn(BTN_PRI, BTN_SM)}
                  onClick={() => {
                    onClose();
                    navigate(`/pre-call/${conflict.conflictingAssignmentId}`);
                  }}
                >
                  {t('bookings.detail.conflict.backToCurrent')}
                </button>
                <button
                  type="button"
                  className={cn(BTN_SEC, BTN_SM)}
                  onClick={() => releaseAndRetryMut.mutate(conflict.conflictingAssignmentId)}
                  disabled={releaseAndRetryMut.isPending}
                >
                  {t('bookings.detail.conflict.cancelAndOpen')}
                </button>
                <button type="button" className={BTN_GHO} onClick={() => setConflict(null)}>
                  {t('bookings.detail.conflict.dismiss')}
                </button>
              </div>
            </div>
          </Alert>
        )}

        {/* Section 6 — Action row */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {canStart && (
            <button
              type="button"
              className={cn(BTN_PRI, BTN_SM)}
              onClick={() => startMut.mutate()}
              disabled={startMut.isPending || updateMut.isPending}
            >
              <PhoneCall className="h-4 w-4" aria-hidden />
              {t('bookings.detail.action.start')}
            </button>
          )}
          <button
            type="button"
            className={cn(BTN_SEC, BTN_SM)}
            onClick={() => {
              setRescheduleOpen((v) => !v);
              if (!rescheduleOpen) {
                // Pre-fill with current scheduled date/time on open
                const local = new Date(booking.scheduledAt);
                const yyyy = local.getFullYear();
                const mm = String(local.getMonth() + 1).padStart(2, '0');
                const dd = String(local.getDate()).padStart(2, '0');
                const hh = String(local.getHours()).padStart(2, '0');
                const mi = String(local.getMinutes()).padStart(2, '0');
                setNewDate(`${yyyy}-${mm}-${dd}`);
                setNewTime(`${hh}:${mi}`);
                setNewNotes(booking.notes ?? '');
                setError('');
              }
            }}
            disabled={updateMut.isPending || startMut.isPending}
          >
            {t('bookings.detail.action.reschedule')}
          </button>
          <button
            type="button"
            className={cn(BTN_SEC, BTN_SM)}
            onClick={() => setConfirmAction('completed')}
            disabled={updateMut.isPending}
          >
            {t('bookings.detail.action.markCompleted')}
          </button>
          <button
            type="button"
            className={cn(BTN_SEC, BTN_SM)}
            onClick={() => setConfirmAction('missed')}
            disabled={updateMut.isPending}
          >
            {t('bookings.detail.action.markMissed')}
          </button>
          <button
            type="button"
            className={cn(BTN_SEC, BTN_SM)}
            onClick={() => setConfirmAction('cancelled')}
            disabled={updateMut.isPending}
          >
            {t('bookings.detail.action.markCancelled')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex items-center gap-1 text-xs text-nav-slate hover:text-nav-navy underline-offset-2 hover:underline"
          >
            <X className="h-3 w-3" />
            {t('bookings.detail.action.close')}
          </button>
        </div>

        {/* Inline reschedule form (expands below action row, doesn't replace detail) */}
        {rescheduleOpen && (
          <div className="rounded-[9px] border border-[#c9e2e2] bg-nav-tealTint/60 px-4 py-3 space-y-3">
            <p className="font-head text-sm font-bold text-nav-tealDark m-0">
              {t('bookings.detail.reschedule.title')}
            </p>
            {error && (
              <Alert variant="error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={FIELD_LABEL}>
                  {t('bookings.detail.reschedule.dateLabel')}
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className={INP}
                  disabled={updateMut.isPending}
                />
              </div>
              <div>
                <label className={FIELD_LABEL}>
                  {t('bookings.detail.reschedule.timeLabel')}
                </label>
                <input
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className={INP}
                  disabled={updateMut.isPending}
                />
              </div>
            </div>
            <div>
              <label className={FIELD_LABEL}>
                {t('bookings.detail.reschedule.notesLabel')}
              </label>
              <textarea
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                className={cn(INP, 'resize-none')}
                rows={3}
                disabled={updateMut.isPending}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className={cn(BTN_PRI, BTN_SM)}
                onClick={handleReschedule}
                disabled={updateMut.isPending}
              >
                {t('bookings.edit.save')}
              </button>
              <button
                type="button"
                className={cn(BTN_SEC, BTN_SM)}
                onClick={() => {
                  setRescheduleOpen(false);
                  setNewDate('');
                  setNewTime('');
                  setError('');
                }}
                disabled={updateMut.isPending}
              >
                {t('row.cancel')}
              </button>
            </div>
          </div>
        )}

        <ConfirmDialog
          open={confirmAction !== null}
          tone={
            confirmAction === 'cancelled'
              ? ('danger' as ConfirmTone)
              : confirmAction === 'missed'
                ? ('warning' as ConfirmTone)
                : ('neutral' as ConfirmTone)
          }
          title={
            confirmAction === 'cancelled'
              ? t('bookings.confirm.cancel.title')
              : confirmAction === 'missed'
                ? t('bookings.confirm.noShow.title')
                : t('bookings.confirm.completed.title')
          }
          message={
            confirmAction === 'cancelled'
              ? t('bookings.confirm.cancel.message')
              : confirmAction === 'missed'
                ? t('bookings.confirm.noShow.message')
                : t('bookings.confirm.completed.message')
          }
          loading={updateMut.isPending}
          onConfirm={() => {
            if (!confirmAction) return;
            updateMut.mutate(
              { status: confirmAction },
              {
                onSettled: () => setConfirmAction(null),
              },
            );
          }}
          onCancel={() => {
            if (updateMut.isPending) return;
            setConfirmAction(null);
          }}
        />
      </div>
    </div>
  );
}

/* ----------------------------- Create form ------------------------------ */

/**
 * Inline "+ New booking" form rendered inside the BookingsListModal. Lets
 * the agent type-ahead a patient (last name or MRN), pick a date/time, and
 * submit. On success, the bookings list cache is invalidated and the form
 * closes, dropping the user back to the list.
 *
 * Patients are scoped server-side: agents see only patients they have an
 * assignment for; admins/supervisors see all non-deleted clients.
 */
function CreateBookingForm({
  onCancel,
  onSuccess,
}: {
  onCancel: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState<PatientSearchResult | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<PatientSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Debounce the query input by 250ms.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  // Trigger a search when the debounced query changes.
  useEffect(() => {
    if (selected) return; // hide search list once a patient is picked
    if (debouncedQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    bookingsApi
      .searchPatients(debouncedQuery, 20)
      .then((rows) => {
        if (cancelled) return;
        setSearchResults(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchResults([]);
      })
      .finally(() => {
        if (cancelled) return;
        setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, selected]);

  const createMut = useMutation({
    mutationFn: async (input: { patientId: string; scheduledAt: string; notes?: string }) => {
      return bookingsApi.create({
        patientId: input.patientId,
        scheduledAt: input.scheduledAt,
        notes: input.notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings.me'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['bookings.due-soon'] });
      toast.success(t('bookings.modal.create.successToast'));
      onSuccess();
    },
    onError: (err) => {
      // Surface dedup conflicts inline; everything else as toast.
      if (err instanceof ApiError && err.status === 409) {
        const code = (err.payload as Record<string, unknown> | null)?.code;
        if (code === 'BOOKING_DUPLICATE') {
          setError(t('bookings.modal.create.dedupConflict'));
          return;
        }
        if (code === 'AGENT_TIME_CONFLICT') {
          setError(t('bookings.modal.create.timeConflict'));
          return;
        }
      }
      toast.error('Error', (err as Error).message);
    },
  });

  const handleSubmit = (): void => {
    setError('');
    if (!selected || !date || !time) {
      setError(t('bookings.modal.create.errorRequired'));
      return;
    }
    const dt = new Date(`${date}T${time}`);
    if (Number.isNaN(dt.getTime()) || dt <= new Date()) {
      setError(t('bookings.modal.create.errorPast'));
      return;
    }
    createMut.mutate({
      patientId: selected.id,
      scheduledAt: dt.toISOString(),
      notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className={BTN_GHO}
          data-testid="bookings-create-back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          {t('bookings.modal.create.cancel')}
        </button>
      </div>

      <div className="rounded-[9px] border border-[#c9e2e2] bg-nav-tealTint/50 px-4 py-3">
        <h3 className="font-head text-sm font-bold text-nav-tealDark m-0">
          {t('bookings.modal.create.heading')}
        </h3>
        <p className="text-xs text-nav-slate mt-0.5">
          {t('bookings.modal.create.subtitle')}
        </p>
      </div>

      {error && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Patient — search OR selected */}
      <div className="space-y-2">
        <label className={cn(FIELD_LABEL, 'mb-0')}>
          {t('bookings.modal.create.patientLabel')}
        </label>

        {selected ? (
          <div className="rounded-md border border-nav-sage bg-nav-sageTint/60 px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <User className="h-4 w-4 text-nav-sageMid shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-nav-navy truncate">
                  {selected.lastName}, {selected.firstName}
                </p>
                <p className="text-[11px] font-mono text-nav-slate">
                  MRN: …{selected.cinLast6}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setQuery('');
                setSearchResults(null);
              }}
              className="text-[11px] text-nav-tealDark hover:text-nav-navy underline-offset-2 hover:underline"
              data-testid="bookings-create-patient-change"
            >
              {t('bookings.modal.create.patientChange')}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-nav-faint pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('bookings.modal.create.patientSearchPlaceholder')}
                aria-label={t('bookings.modal.create.patientSearchPlaceholder')}
                data-testid="bookings-create-patient-search"
                className={cn(INP, 'pl-8 pr-9')}
                autoFocus
              />
              {searching && (
                <span
                  aria-hidden="true"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-nav-teal animate-pulse"
                />
              )}
            </div>
            {debouncedQuery.length >= 1 && debouncedQuery.length < 2 && (
              <p className="text-[11px] text-nav-slate">{t('bookings.modal.create.searchHint')}</p>
            )}
            {searchResults !== null && (
              <div className="rounded-md border border-nav-card bg-white max-h-56 overflow-y-auto modal-scroll divide-y divide-nav-hair">
                {searchResults.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-nav-slate italic" data-testid="bookings-create-search-empty">
                    {t('bookings.modal.create.searchEmpty')}
                  </div>
                ) : (
                  searchResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        setSelected(r);
                        setSearchResults(null);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-nav-tealTint/60 focus:bg-nav-tealTint focus:outline-none transition-colors"
                      data-testid="bookings-create-search-result"
                    >
                      <p className="text-sm text-nav-navy">
                        <span className="font-medium">{r.lastName}</span>, {r.firstName}
                      </p>
                      <p className="text-[11px] text-nav-slate font-mono">
                        MRN: …{r.cinLast6}
                      </p>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Date + Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL}>
            {t('bookings.modal.create.dateLabel')}
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={INP}
            data-testid="bookings-create-date"
            disabled={createMut.isPending}
          />
        </div>
        <div>
          <label className={FIELD_LABEL}>
            {t('bookings.modal.create.timeLabel')}
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className={INP}
            data-testid="bookings-create-time"
            disabled={createMut.isPending}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className={FIELD_LABEL}>
          {t('bookings.modal.create.notesLabel')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          maxLength={500}
          rows={3}
          placeholder={t('bookings.modal.create.notesPlaceholder')}
          className={cn(INP, 'resize-none')}
          data-testid="bookings-create-notes"
          disabled={createMut.isPending}
        />
        <p className="text-[11px] text-nav-faint text-right mt-1">{notes.length}/500</p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          className={cn(BTN_SEC, BTN_SM)}
          onClick={onCancel}
          disabled={createMut.isPending}
          data-testid="bookings-create-cancel"
        >
          {t('bookings.modal.create.cancel')}
        </button>
        <button
          type="button"
          className={cn(BTN_PRI, BTN_SM)}
          onClick={handleSubmit}
          disabled={createMut.isPending || !selected || !date || !time}
          data-testid="bookings-create-confirm"
        >
          {t('bookings.modal.create.confirm')}
        </button>
      </div>
    </div>
  );
}

